/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    EnvelopeIcon,
    UserGroupIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { invitationService } from '../services/api';
import { InvitationDetails } from '../types';
import { useAuthStore } from '../store/authStore';
import { FloatingPixelBackground, PixelCorner } from '../components/pixel/PixelComponents';

const AcceptInvitation: React.FC = () => {
    const { isAuthenticated, user, checkAuth } = useAuthStore();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';

    const [details, setDetails] = useState<InvitationDetails | null>(null);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState('');

    // This page is a public route rendered outside DashboardLayout, so the
    // auth store's rehydration/checkAuth may not have run yet. Kick it off
    // if we don't already believe the user is authenticated, so a logged-in
    // user isn't wrongly shown "Sign in to accept".
    useEffect(() => {
        if (!isAuthenticated) {
            checkAuth();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!token) {
            setError('Missing invitation token');
            return;
        }
        invitationService.getInvitation(token)
            // Backend wraps the payload: { invitation: { scope, email, ... } }
            .then((res) => setDetails(res.data.invitation))
            .catch((e: any) => setError(e.response?.data?.error || 'Invitation not found or expired'));
    }, [token]);

    const handleSignInFirst = () => {
        sessionStorage.setItem('pending_invitation_token', token);
        navigate('/login');
    };

    const handleAccept = async () => {
        setAccepting(true);
        try {
            await invitationService.acceptInvitation(token);
            toast.success('Invitation accepted!');
            navigate('/');
        } catch (e: any) {
            setError(e.response?.data?.error || 'Failed to accept invitation');
        } finally {
            setAccepting(false);
        }
    };

    const handleGoToLogin = () => {
        navigate('/login');
    };

    const emailMismatch = isAuthenticated && !!user?.email && !!details?.email
        && user.email.toLowerCase() !== details.email.toLowerCase();

    const orgLabel = (role: string) => role === 'org_admin' || role === 'org_owner' ? 'Admin' : role === 'org_billing_admin' ? 'Billing Admin' : 'Member';
    const projectLabel = (role: string) => role === 'project_admin' ? 'Admin' : 'Member';

    return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
            <FloatingPixelBackground intensity="medium" />

            {/* Pixel corner decorations - hidden on very small screens */}
            <div className="hidden sm:block">
                <PixelCorner position="top-left" />
                <PixelCorner position="top-right" />
                <PixelCorner position="bottom-left" />
                <PixelCorner position="bottom-right" />
            </div>

            <div className="max-w-md w-full relative z-10">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500 rounded-2xl mb-4">
                        <UserGroupIcon className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-white">
                        {details?.scope === 'organization' ? 'Organization Invitation' : 'Project Invitation'}
                    </h1>
                    <p className="text-gray-400 mt-1">You've been invited to collaborate</p>
                </div>

                <div className="bg-gray-900 rounded-2xl shadow-xl overflow-hidden border border-gray-800">
                    {/* Loading State */}
                    {!details && !error && (
                        <div className="p-8 text-center">
                            <div className="animate-spin w-12 h-12 border-4 border-gray-700 border-t-primary-500 rounded-full mx-auto mb-4"></div>
                            <p className="text-gray-400">Loading invitation...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
                            </div>
                            <h2 className="text-xl font-semibold text-white mb-2">Invalid Invitation</h2>
                            <p className="text-gray-400 mb-6">
                                {error || 'This invitation link is invalid or has expired.'}
                            </p>

                            <button
                                onClick={handleGoToLogin}
                                className="w-full btn-primary"
                            >
                                Go to Login
                            </button>
                        </div>
                    )}

                    {/* Details + Action State */}
                    {details && !error && (
                        <div className="p-8">
                            <div className="p-4 bg-gray-800 border border-gray-700 rounded-xl mb-6">
                                <h3 className="font-semibold text-white mb-2">
                                    {details.scope === 'organization' ? details.organization_name : details.project_name}
                                </h3>
                                <p className="text-sm text-gray-300">
                                    Invited by <span className="font-medium">{details.inviter}</span>
                                </p>
                                <p className="text-sm text-gray-400 mt-1">
                                    Role: <span className="capitalize font-medium">
                                        {details.scope === 'organization' ? orgLabel(details.org_role) : projectLabel(details.role)}
                                    </span>
                                </p>
                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-700">
                                    <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-300">{details.email}</span>
                                </div>
                            </div>

                            {!isAuthenticated && (
                                <button
                                    onClick={handleSignInFirst}
                                    className="w-full btn-primary flex items-center justify-center gap-2"
                                >
                                    Sign in to accept
                                </button>
                            )}

                            {isAuthenticated && emailMismatch && (
                                <div className="p-4 bg-yellow-900/30 border border-yellow-700 rounded-xl flex items-start gap-3">
                                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-yellow-200">
                                        This invitation was sent to {details.email}. Sign out and sign in with that email.
                                    </p>
                                </div>
                            )}

                            {isAuthenticated && !emailMismatch && (
                                <button
                                    onClick={handleAccept}
                                    disabled={accepting}
                                    className="w-full btn-primary flex items-center justify-center gap-2"
                                >
                                    {accepting ? (
                                        <>
                                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                                            Accepting...
                                        </>
                                    ) : (
                                        <>
                                            Accept invitation
                                            <CheckCircleIcon className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <p className="text-center text-sm text-gray-400 mt-6">
                    Need help?{' '}
                    <a href="mailto:support@podstack.ai" className="text-primary-500 hover:text-primary-500">
                        Contact Support
                    </a>
                </p>
            </div>
        </div>
    );
};

export default AcceptInvitation;
